import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { api } from "../../lib/api";
import { BOOKING } from "../../data/content";
import { Reveal, Eyebrow } from "./Reveal";

const SPEAK_IMG =
  "https://images.unsplash.com/photo-1662089691295-7ef1baa0d0d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const EVENT_TYPES = [
  "Keynote Speaker",
  "Panelist",
  "Educational Workshop",
  "Press / Media",
];

const Field = ({ no, label, children }) => (
  <label className="block">
    <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#5c6474]">
      {no ? <span className="mr-2 font-mono text-[#9c7a33]">{no}</span> : null}
      {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full border-0 border-b border-[var(--line)] bg-transparent pb-3 pt-1 text-[#1c2b4a] outline-none transition-colors duration-300 placeholder:text-[#1c2b4a]/25 focus:border-[#9c7a33]";

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    event_type: "",
    event_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", form);
      setDone(true);
      toast.success("Inquiry sent — Adonica's team will be in touch shortly.");
      setForm({
        name: "",
        email: "",
        organization: "",
        event_type: "",
        event_date: "",
        message: "",
      });
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-40"
    >
      <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
        {/* Left */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <Eyebrow testid="booking-eyebrow">{BOOKING.eyebrow}</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-7 font-display text-4xl font-light tracking-tight text-[#1c2b4a] md:text-5xl lg:text-6xl">
                {BOOKING.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#5c6474] md:text-lg">
                {BOOKING.body}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap gap-2">
                {BOOKING.topics.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-[var(--line)] px-4 py-2 text-xs text-[#1c2b4a]/80"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="relative mt-10">
                <div className="absolute -left-3 -top-3 hidden h-full w-full rounded-sm border border-[#9c7a33]/45 lg:block" />
                <div className="relative overflow-hidden rounded-sm border border-[var(--line)] bg-white p-2">
                  <img
                    src={SPEAK_IMG}
                    alt="Adonica Shaw speaking"
                    className="h-64 w-full rounded-sm object-cover"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Right — inquiry desk */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form
              data-testid="booking-form"
              onSubmit={submit}
              className="rounded-sm border border-[var(--line)] bg-[#ffffff] p-8 md:p-12"
            >
              <div className="mb-10 flex items-center justify-between border-b border-[var(--line)] pb-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#9c7a33]">
                  Inquiry Desk
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#5c6474]">
                  Keynotes · Workshops · Press
                </span>
              </div>

              <div className="grid gap-8 sm:grid-cols-2">
                <Field no="01" label="Full name *">
                  <input
                    data-testid="booking-name"
                    className={inputCls}
                    placeholder="Your name"
                    value={form.name}
                    onChange={set("name")}
                  />
                </Field>
                <Field no="02" label="Email *">
                  <input
                    data-testid="booking-email"
                    type="email"
                    className={inputCls}
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={set("email")}
                  />
                </Field>
                <Field no="03" label="Organization">
                  <input
                    data-testid="booking-organization"
                    className={inputCls}
                    placeholder="Company / event"
                    value={form.organization}
                    onChange={set("organization")}
                  />
                </Field>
                <Field no="04" label="Preferred date">
                  <input
                    data-testid="booking-event-date"
                    className={inputCls}
                    placeholder="e.g. March 2027"
                    value={form.event_date}
                    onChange={set("event_date")}
                  />
                </Field>
                <div className="sm:col-span-2">
                  <span className="mb-2 block text-[11px] uppercase tracking-[0.2em] text-[#5c6474]">
                    <span className="mr-2 font-mono text-[#9c7a33]">05</span>
                    Inquiry type
                  </span>
                  <div data-testid="booking-event-type" className="mt-1 flex flex-wrap gap-2">
                    {EVENT_TYPES.map((t) => (
                      <button
                        type="button"
                        key={t}
                        data-testid={`booking-event-type-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            event_type: f.event_type === t ? "" : t,
                          }))
                        }
                        className={`rounded-full border px-4 py-2 text-xs transition-colors duration-300 ${
                          form.event_type === t
                            ? "border-[#9c7a33] bg-[#9c7a33] text-white"
                            : "border-[var(--line)] text-[#1c2b4a]/80 hover:border-[#9c7a33]/60"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Field no="06" label="Tell us about your event *">
                    <textarea
                      data-testid="booking-message"
                      rows={4}
                      className={`${inputCls} resize-none`}
                      placeholder="Audience, theme, format and anything else we should know…"
                      value={form.message}
                      onChange={set("message")}
                    />
                  </Field>
                </div>
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={loading || done}
                className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#9c7a33] px-8 py-4 text-sm uppercase tracking-[0.16em] text-[#f4f2ec] transition-colors duration-300 hover:bg-[#7e6128] disabled:opacity-70 sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending
                  </>
                ) : done ? (
                  <>
                    <Check className="h-4 w-4" /> Sent
                  </>
                ) : (
                  "Send Inquiry"
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
